import React, { useCallback, useContext, useMemo } from "react";
import ReactDOM from "react-dom";
import Chat from "components/tmisc/chat/Chat";
import { HostControllerContext } from "components/presentation/main/Main.lib.communication";
import { useViewSlotNode } from "components/presentation/main/Main.lib.viewSlots";

// Mounts <Chat /> instances declared by the project's app inside the placeholders it
// rendered (`<div data-seed-chat="main" />`, produced by the runtime's /helpers/Chat).
//
// Chat stays in the host's React tree — it keeps Apollo, the websocket, auth and
// sessionStorage — while its DOM is a child of the app's own markup, so layout, scroll and
// resize are resolved natively by the browser. Both trees share one document, so there is
// nothing to sync: the app's CSS and the host's are already on the same page.

const ChatPortal = ({ containerNode, slotId, chatProps, flow }) => {

  const host = useContext(HostControllerContext);
  const selector = useMemo(() => `[data-seed-chat="${slotId}"]`, [slotId]);
  const targetNode = useViewSlotNode({ containerNode, selector });

  // Hands the conversation's imperative API to the host, keyed by slot, so an action the
  // app sends (a hidden message, the answer to a component) reaches this chat and not
  // another one. React calls it with null on unmount, which unregisters the slot.
  const registerApi = useCallback(
    (api) => host?.registerChatApi?.(slotId, api),
    [host, slotId]
  );

  if (!targetNode || !targetNode.isConnected) return null;
  return ReactDOM.createPortal(
    <Chat ref={registerApi} flow={flow} slotId={slotId} {...chatProps} />,
    targetNode
  );
}

const ChatPortalManager = ({ containerNode, chats, flow }) => {

  if (!containerNode || !chats) return null;

  return Object.entries(chats).map(([slotId, chatProps]) => (
    <ChatPortal
      key={slotId}
      containerNode={containerNode}
      slotId={slotId}
      chatProps={chatProps}
      flow={flow}
    />
  ));
}

export default ChatPortalManager;
